import { useEffect, useState } from 'react';
import axios from 'axios';
import { MdPlayArrow } from 'react-icons/md';
import { CodeBlock } from 'components/apiKeyScope/ApiEndpointPlayground';
import { CONFIG } from 'variables/config';
import { useIndustries, resolveFields, resolveMandatoryFields } from 'services/industryService';
import { fieldExampleValue, EMAIL_FIELD_REGEX, TEST_EMAIL } from 'services/formFieldExamples';

// Same convention as ApiEndpointPlayground/edit.tsx's DISPLAY_API_URL.
const DISPLAY_API_URL =
    CONFIG.apiUrl.replace(/\/$/, '') === 'https://api-leadrs-co.osc-fr1.scalingo.io'
        ? 'https://api.leadrs.co'
        : CONFIG.apiUrl.replace(/\/$/, '');

interface FormBuilderField {
    id: string;
    name: string;
    type?: string;
    options?: string[];
}

interface SubmitLeadTesterProps {
    apiKey: string;
    formFields: FormBuilderField[];
    // The product chosen elsewhere on the page (step 2 of the guide): prefills the tester's id.
    defaultSubIndustryId?: number | null;
}

// Bespoke tester for POST /lead/api/submit — unlike ApiEndpointPlayground (a fixed set of
// path/query/body params passed in by the caller), this endpoint's body fields depend on a value
// the user types INTO the tester itself (subIndustryId): typing a new id has to re-resolve which
// fields even exist, and rebuild the body around them. That "the field list changes based on one
// of its own inputs" behavior doesn't fit ApiEndpointPlayground's fixed prop-driven bodyFields.
const SubmitLeadTester = ({ apiKey, formFields, defaultSubIndustryId }: SubmitLeadTesterProps) => {
    const { industries } = useIndustries();
    const [subIndustryIdInput, setSubIndustryIdInput] = useState('');
    useEffect(() => {
        if (defaultSubIndustryId != null) setSubIndustryIdInput(String(defaultSubIndustryId));
    }, [defaultSubIndustryId]);
    const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
    const [sending, setSending] = useState(false);
    const [response, setResponse] = useState<{ status: number | null; data: any } | null>(null);

    const parsedId = subIndustryIdInput.trim() ? Number(subIndustryIdInput) : null;
    const validId = parsedId != null && Number.isFinite(parsedId);

    let resolvedIndustry: string | null = null;
    let resolvedSubIndustry: string | null = null;
    if (validId) {
        for (const industry of industries) {
            const sub = industry.subIndustries.find((s) => s.subIndustryId === parsedId);
            if (sub) {
                resolvedIndustry = industry.name;
                resolvedSubIndustry = sub.name;
                break;
            }
        }
    }
    const industryObj = industries.find((i) => i.name === resolvedIndustry) || null;
    const mandatoryFieldIds = resolvedIndustry ? resolveMandatoryFields(industryObj, resolvedSubIndustry || undefined) : [];
    const fieldLabel = (fieldId: string) => formFields.find((f) => f.id === fieldId)?.name || fieldId;
    const fieldDef = (label: string) => formFields.find((f) => f.name === label);

    // Re-seed the field inputs whenever the RESOLVED product changes (not on every keystroke) —
    // fields the new product doesn't need are dropped, fields it needs but didn't have a value
    // yet get a sensible example (the test address for an email-looking field, so a first "Envoyer"
    // click is always safe — see TEST_EMAIL).
    useEffect(() => {
        if (!resolvedIndustry) {
            setFieldValues({});
            return;
        }
        setFieldValues((prev) => {
            const next: Record<string, string> = {};
            mandatoryFieldIds.forEach((id) => {
                const label = fieldLabel(id);
                if (prev[label] !== undefined) {
                    next[label] = prev[label];
                    return;
                }
                next[label] = EMAIL_FIELD_REGEX.test(label) ? TEST_EMAIL : fieldExampleValue(label, fieldDef(label));
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedIndustry, resolvedSubIndustry]);

    const requestBody: Record<string, string | number> = { subIndustryId: parsedId ?? ('<subIndustryId>' as any) };
    mandatoryFieldIds.forEach((id) => {
        const label = fieldLabel(id);
        requestBody[label] = fieldValues[label] ?? '';
    });

    const url = `${CONFIG.apiUrl}/lead/api/submit`;
    const displayUrl = `${DISPLAY_API_URL}/lead/api/submit`;
    const displayKey = apiKey.trim() || '<votre-cle-api>';
    const curlSnippet = `curl -X POST "${displayUrl}" \\\n  -H "x-api-key: ${displayKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(requestBody)}'`;
    const fetchSnippet = `fetch("${displayUrl}", {\n  method: "POST",\n  headers: {\n    "x-api-key": "${displayKey}",\n    "Content-Type": "application/json"\n  },\n  body: JSON.stringify(${JSON.stringify(requestBody, null, 2)})\n})\n  .then((res) => res.json())\n  .then((data) => console.log(data));`;

    const handleSend = async () => {
        setSending(true);
        setResponse(null);
        try {
            const res = await axios.post(url, requestBody, { headers: { 'x-api-key': apiKey.trim() } });
            setResponse({ status: res.status, data: res.data });
        } catch (e: any) {
            setResponse({ status: e?.response?.status ?? null, data: e?.response?.data ?? { message: e?.message || 'Erreur réseau' } });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5">
            <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-leadrs-yellow-soft px-2 py-0.5 font-mono text-xs font-bold text-leadrs-yellow-focus">POST</span>
                <code className="font-mono text-sm text-leadrs-text">/lead/api/submit</code>
            </div>
            <h4 className="mt-2 font-geist text-base font-semibold text-leadrs-text">Envoyer un lead</h4>
            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                Tapez un <code className="font-mono text-leadrs-brand">subIndustryId</code> : le body se construit
                automatiquement avec les champs obligatoires du produit correspondant. Le champ e-mail est pré-rempli
                avec l'adresse de test Leadrs — un premier essai n'est jamais un vrai lead facturé.
            </p>

            <div className="mt-4">
                <label className="font-geist text-xs font-medium text-leadrs-text-soft">
                    subIndustryId <span className="text-leadrs-red-focus">*</span>
                </label>
                <input
                    type="number"
                    value={subIndustryIdInput}
                    onChange={(e) => setSubIndustryIdInput(e.target.value)}
                    placeholder="ex: 12"
                    className="mt-1 w-full max-w-[160px] rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text sm:w-40"
                />
                {subIndustryIdInput.trim() && !resolvedIndustry && (
                    <p className="mt-1.5 font-geist text-xs text-leadrs-red-focus">subIndustryId inconnu.</p>
                )}
                {resolvedIndustry && (
                    <p className="mt-1.5 font-geist text-xs text-leadrs-text-soft">
                        Produit : <span className="font-semibold text-leadrs-text">{resolvedIndustry}</span>
                        {resolvedSubIndustry ? ` / ${resolvedSubIndustry}` : ''}
                    </p>
                )}
            </div>

            {resolvedIndustry && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {mandatoryFieldIds.map((id) => {
                        const label = fieldLabel(id);
                        const def = fieldDef(label);
                        const isEnum = (def?.type === 'select' || def?.type === 'multi-select') && (def?.options?.length || 0) > 0;
                        return (
                            <div key={id}>
                                <label className="font-geist text-xs font-medium text-leadrs-text-soft">
                                    {label} <span className="text-leadrs-red-focus">*</span>
                                </label>
                                {isEnum ? (
                                    <select
                                        value={fieldValues[label] ?? ''}
                                        onChange={(e) => setFieldValues((prev) => ({ ...prev, [label]: e.target.value }))}
                                        className="mt-1 w-full rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text"
                                    >
                                        {def!.options!.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={fieldValues[label] ?? ''}
                                        onChange={(e) => setFieldValues((prev) => ({ ...prev, [label]: e.target.value }))}
                                        className="mt-1 w-full rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text"
                                    />
                                )}
                                {isEnum && (
                                    <p className="mt-1 font-geist text-[11px] text-leadrs-text-soft">
                                        Valeurs possibles : {def!.options!.join(', ')}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-4">
                <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">URL</p>
                <code className="mt-1 block overflow-x-auto whitespace-nowrap rounded-md bg-leadrs-bg px-3 py-2 font-mono text-xs text-leadrs-text">
                    {displayUrl}
                </code>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                    <p className="mb-1 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">curl</p>
                    <CodeBlock code={curlSnippet} />
                </div>
                <div>
                    <p className="mb-1 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">fetch</p>
                    <CodeBlock code={fetchSnippet} />
                </div>
            </div>

            <div className="mt-4 border-t border-leadrs-stroke pt-4">
                <div className="flex items-center justify-between">
                    <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Tester en direct</p>
                    <button
                        onClick={handleSend}
                        disabled={sending || !apiKey.trim() || !validId}
                        className="linear flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-3 py-1.5 font-geist text-xs font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                    >
                        <MdPlayArrow className="h-4 w-4" />
                        {sending ? 'Envoi...' : 'Envoyer la requête'}
                    </button>
                </div>
                {!apiKey.trim() && (
                    <p className="mt-2 font-geist text-xs text-leadrs-text-soft">
                        Renseignez votre clé API en haut de page pour tester cette requête.
                    </p>
                )}
                {apiKey.trim() && !validId && (
                    <p className="mt-2 font-geist text-xs text-leadrs-red-focus">Indiquez un subIndustryId valide.</p>
                )}
                {response && (
                    <div className="mt-3">
                        <span
                            className={`rounded-full px-2 py-0.5 font-mono text-xs font-bold ${
                                response.status && response.status < 400
                                    ? 'bg-leadrs-green-soft text-leadrs-green-focus'
                                    : 'bg-leadrs-red-soft text-leadrs-red-focus'
                            }`}
                        >
                            {response.status ?? 'Erreur réseau'}
                        </span>
                        <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-leadrs-bg px-3 py-2 font-mono text-xs text-leadrs-text">
                            {JSON.stringify(response.data, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubmitLeadTester;
